/* eslint-disable no-restricted-syntax */
import { Loader } from '@googlemaps/js-api-loader';
import { createValidationRule } from '../forms/forms.utils';
import { VALIDATION_RULE_IS_NOT_CYRILLIC } from '../../constants/validation';
import { envConfig } from '../../config';
import { store } from '../../stores/store';
import { normalizeVisuallySimilarLatin } from '../generic/generic.utils';

const loader = new Loader({
  apiKey: envConfig.REACT_APP_GOOGLE_MAP_API_KEY ?? '',
  version: 'weekly',
  libraries: ['places', 'geocoding'],
});

// eslint-disable-next-line import/no-mutable-exports
export let autocompleteService: google.maps.places.AutocompleteService;
// eslint-disable-next-line import/no-mutable-exports
export let geocoder: google.maps.Geocoder;

// eslint-disable-next-line unused-imports/no-unused-vars
let didInitPlacesLibs = false;

export const loadPlacesLibs = async () => {
  const importPlaces = loader.importLibrary('places');
  const importGeocoder = loader.importLibrary('geocoding');

  // Promise all to fix warning caused by loading 2 libs
  Promise.all([importPlaces, importGeocoder]).then(() => {
    autocompleteService = new google.maps.places.AutocompleteService();
    geocoder = new google.maps.Geocoder();
    didInitPlacesLibs = true;
  });
};

export const getGeocodingInfo = async (
  address?: string,
  placeId?: string
): Promise<google.maps.GeocoderResult | undefined> => {
  if (!address && !placeId) return;

  try {
    const request = {
      address,
      placeId,
      language: 'en',
    } as google.maps.GeocoderRequest;
    const { results } = await geocoder.geocode(request);

    if (results.length > 0) return results[0];
  } catch (error) {
    console.error(
      `Failed to retrieve geocoding info for address: ${address} and/or placeId=${placeId}`
    );
  }
};

export const getPredictions = async (
  value: string,
  predictionTypes: string[] = ['address'],
  countryConstraint?: string | string[] | null,
  cityName?: string
) => {
  if (!autocompleteService) return [];

  let autocompleteRequest: google.maps.places.AutocompletionRequest = {
    input: value,
    types: predictionTypes, // Specify the type of predictions you want (e.g., addresses)
    language: store.localizationsStore.selectedLocalization?.code ?? 'en',
  };

  if (countryConstraint) {
    autocompleteRequest = {
      ...autocompleteRequest,
      componentRestrictions: {
        country: countryConstraint,
      },
    };
  }

  if (cityName) {
    const geocodeInfo = await getGeocodingInfo(cityName);
    if (geocodeInfo) {
      autocompleteRequest = {
        ...autocompleteRequest,
        locationRestriction: geocodeInfo.geometry.bounds,
      };
    }
  }

  return new Promise<google.maps.places.AutocompletePrediction[]>(
    (resolve, reject) => {
      autocompleteService.getPlacePredictions(
        autocompleteRequest,
        async (receivedPredictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            if (!receivedPredictions) {
              resolve([]);
              return;
            }

            resolve(receivedPredictions);
          } else if (
            status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS
          ) {
            resolve([]);
          } else {
            reject(
              `Failed to get predictions for value: ${value} with status: ${status}`
            );
          }
        }
      );
    }
  );
};

export async function getPlaceDetails(
  placeId: string,
  requiresPostalCode: boolean = false
) {
  if (!geocoder) return null;
  try {
    const geocodeResults: google.maps.GeocoderResult[] = await new Promise(
      (resolve, reject) => {
        geocoder.geocode({ placeId, language: 'en' }, (results, status) => {
          if (status === 'OK') {
            resolve(results || []);
          } else {
            reject(
              `Failed to get place details for placeID: ${placeId} with status: ${status}`
            );
          }
        });
      }
    );

    if (geocodeResults && geocodeResults.length > 0) {
      const addressComponents = geocodeResults[0].address_components;

      const streetComponent = addressComponents.find((component) =>
        component.types.includes('route')
      );

      const postalCodeObj = addressComponents.find((component) =>
        component.types.includes('postal_code')
      );

      const requiredPlaceInfoFound = requiresPostalCode
        ? !!postalCodeObj
        : postalCodeObj || streetComponent;

      if (requiredPlaceInfoFound) {
        return {
          address_components: addressComponents,
          formatted_address: normalizeVisuallySimilarLatin(
            geocodeResults[0].formatted_address
          ),
          postal_code: normalizeVisuallySimilarLatin(
            postalCodeObj?.long_name || null
          ),
          street: normalizeVisuallySimilarLatin(
            streetComponent?.long_name || null
          ),
          location: geocodeResults[0].geometry.location,
          placeId,
        };
      }

      const location = {
        lat: geocodeResults[0].geometry.location.lat(),
        lng: geocodeResults[0].geometry.location.lng(),
      };

      const reverseGeocodeResults: google.maps.GeocoderResult[] =
        await new Promise((resolve, reject) => {
          geocoder.geocode({ location, language: 'en' }, (results, status) => {
            if (status === 'OK') {
              resolve(results || []);
            } else {
              reject(status);
            }
          });
        });

      if (reverseGeocodeResults && reverseGeocodeResults.length > 0) {
        const reverseAddressComponents =
          reverseGeocodeResults[0].address_components;

        const reverseStreetComponent = reverseAddressComponents.find(
          (component) => component.types.includes('route')
        );

        const reversePostalCodeObj = reverseAddressComponents.find(
          (component) => component.types.includes('postal_code')
        );

        return {
          address_components: reverseAddressComponents,
          formatted_address: normalizeVisuallySimilarLatin(
            reverseGeocodeResults[0].formatted_address
          ),
          postal_code: normalizeVisuallySimilarLatin(
            reversePostalCodeObj?.long_name || null
          ),
          street: normalizeVisuallySimilarLatin(
            reverseStreetComponent?.long_name || null
          ),
          location: geocodeResults[0].geometry.location,
          placeId,
        };
        // eslint-disable-next-line no-else-return
      } else {
        console.error('No results found for the given coordinates.');
      }
    } else {
      console.error('No results found for the given placeID.');
    }
  } catch (error) {
    console.error('Geocoder failed due to:', error);
  }

  return null;
}

export async function getAddressComponentsByZipCode(
  zipCode: string,
  countryCode: string,
  config: { includeCity?: boolean; includeRegion?: boolean }
): Promise<{ city?: string; region?: string } | undefined> {
  try {
    const response = await new Promise<any>((resolve, reject) => {
      geocoder.geocode(
        {
          componentRestrictions: {
            country: countryCode,
            postalCode: zipCode,
          },
          language: 'en',
        },
        (
          results: google.maps.GeocoderResult[] | null,
          status: google.maps.GeocoderStatus
        ) => {
          if (status === 'OK' && results !== null) {
            resolve(results);
          } else {
            reject(`Geocoder failed with status: ${status}`);
          }
        }
      );
    });

    const result: { city?: string; region?: string } = {};
    if (response.length > 0) {
      if (config.includeCity) {
        result.city = getCityFromPlaceDetails(
          response[0].address_components,
          countryCode
        );
      }

      if (config.includeRegion) {
        result.region = getRegionFromPlaceDetails(
          response[0].address_components,
          countryCode
        );
      }

      return result;
    }
  } catch (error) {
    console.error('Error fetching region by zip code:', error);
    return undefined;
  }
}

export async function getCityByZipCode(
  zipCode: string,
  countryCode: string
): Promise<string | undefined> {
  return (
    await getAddressComponentsByZipCode(zipCode, countryCode, {
      includeCity: true,
    })
  )?.city;
}

export async function getRegionByZipCode(
  zipCode: string,
  countryCode: string
): Promise<string | undefined> {
  return (
    await getAddressComponentsByZipCode(zipCode, countryCode, {
      includeRegion: true,
    })
  )?.region;
}

export async function getCityAndRegionByZipCode(
  zipCode: string,
  countryCode: string
): Promise<{ city?: string; region?: string } | undefined> {
  const res = await getAddressComponentsByZipCode(zipCode, countryCode, {
    includeCity: true,
    includeRegion: true,
  });

  return res;
}

export function getCityFromPlaceDetails(
  address_components: google.maps.GeocoderAddressComponent[] | undefined,
  countryCode?: string
) {
  if (!address_components) return '';

  const adminAreaOrder =
    countryCode === 'IT'
      ? [
          ['political', 'locality'],
          ['administrative_area_level_2', 'political'],
          ['sublocality_level_1', 'sublocality', 'political'],
          ['neighborhood', 'political'],
          ['postal_town'],
        ]
      : [
          ['political', 'locality'],
          ['sublocality_level_1', 'sublocality', 'political'],
          ['neighborhood', 'political'],
          ['postal_town'],
        ];

  let cityLongName = '';

  for (const adminArea of adminAreaOrder) {
    const cityComp = address_components.find((comp) =>
      adminArea.every((x) => comp.types.includes(x))
    );

    if (cityComp) {
      cityLongName = cityComp.long_name;
      break;
    }
  }
  return normalizeVisuallySimilarLatin(cityLongName);
}

export const getCountryFromPlaceDetails = (
  address_components: google.maps.GeocoderAddressComponent[] | undefined
) => {
  if (!address_components) return '';

  const cityComp = address_components.find(
    (comp) => comp.types.includes('political') && comp.types.includes('country')
  );

  if (!cityComp) return '';
  return normalizeVisuallySimilarLatin(cityComp.short_name);
};

export const getStreetFromPlaceDetails = (
  address_components: google.maps.GeocoderAddressComponent[] | undefined
) => {
  if (!address_components) return '';

  const cityComp = address_components.find((comp) =>
    comp.types.includes('route')
  );

  if (!cityComp) return '';
  return normalizeVisuallySimilarLatin(cityComp.long_name);
};

export function getZipCodeFromPlaceDetails(
  address_components: google.maps.GeocoderAddressComponent[] | undefined
) {
  if (!address_components) return '';

  const cityComp = address_components.find((comp) =>
    comp.types.includes('postal_code')
  );

  if (!cityComp) return '';
  return normalizeVisuallySimilarLatin(cityComp.long_name);
}

export const getBuildingFromPlaceDetails = (
  address_components: google.maps.GeocoderAddressComponent[] | undefined
) => {
  if (!address_components) return '';

  const cityComp = address_components.find((comp) =>
    comp.types.includes('street_number')
  );

  if (!cityComp) return '';
  return normalizeVisuallySimilarLatin(cityComp.long_name);
};

export const getApartmentFromPlaceDetails = (
  address_components: google.maps.GeocoderAddressComponent[] | undefined
) => {
  if (!address_components) return '';

  const cityComp = address_components.find((comp) =>
    comp.types.includes('subpremise')
  );

  if (!cityComp) return '';
  return normalizeVisuallySimilarLatin(cityComp.long_name.split(' ')[1]) ?? '';
};

export function getRegionFromPlaceDetails(
  address_components: google.maps.GeocoderAddressComponent[] | undefined,
  countryCode?: string
) {
  if (!address_components) return '';

  const adminAreaOrder =
    countryCode === 'IT'
      ? [
          'administrative_area_level_3',
          'administrative_area_level_1',
          'administrative_area_level_2',
        ]
      : ['administrative_area_level_1', 'administrative_area_level_2'];

  let regionLongName = '';

  for (const adminArea of adminAreaOrder) {
    const regionComp = address_components.find((comp) =>
      comp.types.includes(adminArea)
    );

    if (regionComp) {
      regionLongName = regionComp.long_name;
      break;
    }
  }

  return normalizeVisuallySimilarLatin(regionLongName);
}

export const tryGetBuildingFromPrediction = (
  prediction: google.maps.places.AutocompletePrediction | null,
  buildingFromDetails?: string
) => {
  if (!prediction || !buildingFromDetails) return '';

  const buildingFromDescription = prediction.description
    .split(', ')
    .find((di) => di === buildingFromDetails);

  return normalizeVisuallySimilarLatin(
    buildingFromDescription &&
      buildingFromDescription !== '' &&
      buildingFromDescription !== buildingFromDetails
      ? buildingFromDescription
      : buildingFromDetails
  );
};

export const getPredictionsWithUserInput = (
  predictions: google.maps.places.AutocompletePrediction[],
  filter: string | undefined,
  minLength: number
): google.maps.places.AutocompletePrediction[] => {
  if (
    predictions.length === 0 &&
    filter &&
    filter.length >= minLength &&
    createValidationRule(VALIDATION_RULE_IS_NOT_CYRILLIC).isValidSync(filter)
  ) {
    return [
      {
        description: filter,
        place_id: filter,
      } as google.maps.places.AutocompletePrediction,
    ];
  }

  return predictions;
};

export async function getPlaceDetailsFromLatLng(
  location: google.maps.LatLngLiteral
) {
  const geocoderResults: google.maps.GeocoderResult[] = await new Promise(
    (resolve, reject) => {
      geocoder.geocode(
        {
          location,
          language: store.localizationsStore.selectedLocalization?.code,
        },
        (results, status) => {
          if (status === 'OK') {
            resolve(results || []);
          } else {
            reject(status);
          }
        }
      );
    }
  );

  if (!geocoderResults?.at(0)) return;

  return geocoderResults[0].address_components;
}

export async function getCityFromLatLng(location: google.maps.LatLngLiteral) {
  return getCityFromPlaceDetails(await getPlaceDetailsFromLatLng(location));
}
